import { WIZARD_CONFIG } from './wizard-config';

export default class WizardView {
    constructor(manager) {
        this.manager = manager;
        this.overlay = null;
        this.content = null;
        this.helpIcon = null;
        this.currentHighlight = null;
        this.sectionHandlers = {
            'stair-type-container': {
                toggle: () => {
                    const toggleDiv = document.querySelector('div[onclick="toggleStairType()"]');
                    if (toggleDiv) toggleDiv.click();
                },
                checkVisible: (el) => el.classList.contains('visible'),
                mobileOnly: true
            },
            'stair-specs-container': {
                toggle: () => {
                    const toggleDiv = document.getElementById('specsChev');
                    if (toggleDiv) toggleDiv.click();
                },
                checkVisible: (el) => el.classList.contains('visible'),
                mobileOnly: true
            },
            'update-address-section': {
                toggle: () => {
                    const toggleDiv = document.querySelector('div[onclick="toggleAddressUpdate()"]');
                    if (toggleDiv) toggleDiv.click();
                },
                checkVisible: (el) => !document.getElementById('address-update-form')?.classList.contains('hidden'),
                mobileOnly: false
            }
        };
    }

    createHelpIcon() {
        if (this.helpIcon) return;
        this.helpIcon = document.createElement('div');
        this.helpIcon.id = 'wizard-help-icon';
        this.helpIcon.className = 'wizard-help-icon';
        this.helpIcon.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" 
                stroke="#5bb752" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12" y2="17"></line>
            </svg>
        `;
        // Add click event listener to the help icon
        this.helpIcon.addEventListener('click', () => {
            this.manager.startWizard();
        });
        document.body.appendChild(this.helpIcon);
    }

    createWizardOverlay() {
        if (this.overlay) return;
        
        this.overlay = document.createElement('div');
        this.overlay.className = 'wizard-overlay';
        document.body.appendChild(this.overlay);
        
        this.content = document.createElement('div');
        this.content.className = 'wizard-content';
        this.content.style.display = 'none';
        document.body.appendChild(this.content);

        // Add click event listener to the overlay
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) {
                this.manager.skipWizard();
            }
        });
    }

    showWizard() {
        if (!this.overlay || !this.content) {
            this.createWizardOverlay();
        }
        this.overlay.classList.add('active');
        console.log('Showing wizard');
    }

    hideWizard() {
        if (this.overlay) {
            this.overlay.classList.remove('active');
        }
        if (this.content) {
            this.content.style.opacity = '0';
            setTimeout(() => {
                this.content.style.display = 'none';
            }, WIZARD_CONFIG.animationDuration);
        }
        this.removeHighlight();
    }

    updateStep(stepConfig) {
        if (!this.content) return;
        
        this.content.style.display = 'block';
        this.content.style.opacity = '1';
        this.content.setAttribute('data-step', this.manager.state.currentStep);
        if (this.manager.state.currentStep === 4) {
            const quoteControls = document.getElementById('quote-controls');
            if (quoteControls) {
                const quoteControlsRect = quoteControls.getBoundingClientRect();
                document.documentElement.style.setProperty('--quote-controls-top', `${quoteControlsRect.top}px`);
            }
        }
        this.content.innerHTML = `
            <div class="wizard-progress">Step ${this.manager.state.currentStep} of 4</div>
            <p>${stepConfig.content}</p>
            <div class="wizard-navigation">
                <button class="wizard-button" id="wizard-skip">Skip Tutorial</button>
                <button class="wizard-button" id="wizard-next">Next</button>
            </div>
        `;

        // Add click event listeners to navigation buttons
        document.getElementById('wizard-skip')?.addEventListener('click', () => {
            this.manager.skipWizard();
        });

        document.getElementById('wizard-next')?.addEventListener('click', () => {
            this.manager.goToStep(this.manager.state.currentStep + 1);
        });

        this.updateContentPosition(stepConfig.targetSection);
    }

    async highlightSection(targetSectionId) {
        console.log('Highlighting section:', targetSectionId);
        this.removeHighlight();
        
        const targetElement = document.getElementById(targetSectionId);
        if (!targetElement) return;
        
        document.body.classList.add('wizard-active');
        
        if (window.innerWidth <= 1024) {
            const controls = document.getElementById('controls');
            if (!controls.classList.contains('open')) {
                document.getElementById('menu-toggle')?.click();
                await new Promise(resolve => setTimeout(resolve, 300));
            }
            
            // Close other sections
            for (const [sectionId, handler] of Object.entries(this.sectionHandlers)) {
                if (sectionId !== targetSectionId) {
                    const element = document.getElementById(sectionId);
                    if (element && handler.checkVisible(element)) {
                        handler.toggle();
                        await new Promise(resolve => setTimeout(resolve, 300));
                    }
                }
            }
            
            // Open target section
            const targetHandler = this.sectionHandlers[targetSectionId];
            if (targetHandler) {
                const element = document.getElementById(targetSectionId);
                if (element && !targetHandler.checkVisible(element)) {
                    targetHandler.toggle();
                    await new Promise(resolve => setTimeout(resolve, 300));
                }
            }
        }
        
        targetElement.classList.add('wizard-highlight');
        this.currentHighlight = targetElement;
        
        this.updateContentPosition(targetSectionId);
    }

    removeHighlight() {
        if (this.currentHighlight) {
            this.currentHighlight.classList.remove('wizard-highlight');
            this.currentHighlight = null;
        }
        document.body.classList.remove('wizard-active');
    }

    updateContentPosition(targetSectionId) {
        const targetElement = document.getElementById(targetSectionId);
        if (!targetElement || !this.content) return;
    
        if (window.innerWidth > 1024) {
            const targetRect = targetElement.getBoundingClientRect();
            const contentWidth = this.content.offsetWidth;
            const contentHeight = this.content.offsetHeight;
    
            let left = targetRect.right + 20;
            let top = targetRect.top;
    
            if (left + contentWidth > window.innerWidth) {
                left = targetRect.left - contentWidth - 20;
            }
    
            top = Math.max(20, Math.min(top, window.innerHeight - contentHeight - 20));
    
            this.content.style.transform = `translate(${left}px, ${top}px)`;
        }
    }

    updateHighlightPosition() {
        if (this.currentHighlight) {
            const targetSectionId = this.currentHighlight.id;
            this.removeHighlight();
            this.highlightSection(targetSectionId).then(() => {
                this.updateContentPosition(targetSectionId);
            });
        }
    }
}